import React from 'react'
import { CheckCircle, Zap, Clock, TrendingUp, Check, Rocket } from 'lucide-react'
import { useTestVariant, useConversion } from '../../contexts/ABTestContext'
import { ShutterstockLogo, GettyImagesLogo, AdobeStockLogo, UnsplashLogo, iStockLogo as IStockLogo } from '../icons'

interface HeroSectionProps {
  onGetStarted: () => void
  onWatchDemo: () => void
}

export const HeroSection: React.FC<HeroSectionProps> = ({ onGetStarted, onWatchDemo }) => {
  const ctaVariant = useTestVariant('hero_cta_text')
  const recordConversion = useConversion()

  const ctaText = ctaVariant === 'A'
    ? 'Start Free Trial - Process 100 Photos FREE'
    : 'Get Started Now - Process 100 Photos FREE'

  const mobileCTAText = ctaVariant === 'A'
    ? 'Start Free Trial'
    : 'Get Started Now'

  const handleGetStarted = () => {
    recordConversion('hero_cta_text', 'hero_cta_click', { 
      ctaText,
      variant: ctaVariant 
    })
    onGetStarted()
  }

  return (
    <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white overflow-x-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-black/20">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-24 lg:pt-20 pb-16 sm:pb-20">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          {/* Left Column - Main Content */}
          <div className="text-center lg:text-left animate-in fade-in slide-in-from-left duration-1000 w-full max-w-full overflow-hidden">
            {/* Social Proof Badge */}
            <div className="inline-flex items-center bg-green-500/20 text-green-300 px-4 py-2 sm:px-5 sm:py-3 rounded-full text-xs sm:text-base font-medium mb-8 sm:mb-10 max-w-full">
              <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="truncate">Join 10,000+ photographers saving hours daily</span>
            </div>

            {/* Main Headline - Simplified for mobile readability */}
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 sm:mb-8 max-w-full break-words">
              Generate Perfect SEO Metadata for
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500"> 10,000+ Photos & Videos</span> in Under 60 Seconds
            </h1>

            {/* Value Proposition Subtitle */}
            <p className="text-base sm:text-xl md:text-2xl text-blue-100 mb-8 sm:mb-10 max-w-full sm:max-w-2xl leading-relaxed">
              Stop spending 20+ hours manually writing titles, descriptions, and keywords.
              Our AI does it instantly with 99.2% accuracy. <span className="text-yellow-300 font-semibold">Stock videos earn 135x more per sale—don't leave money on the table.</span>
            </p>

            {/* Key Benefits - Simplified for mobile */}
            <div className="space-y-4 mb-8 sm:mb-10">
              <div className="flex items-center justify-center lg:justify-start text-sm sm:text-base lg:text-lg max-w-full">
                <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 mr-2 sm:mr-3 flex-shrink-0" />
                <span className="text-center lg:text-left"><strong>20x Faster</strong> • <strong>135x More Revenue Per Video</strong> • <strong>Save 25+ Hours/Week</strong></span>
              </div>
            </div>

            {/* CTA Buttons - Improved mobile hierarchy */}
            <div className="flex flex-col gap-4 sm:gap-5 mb-10 sm:mb-12">
              <button
                onClick={handleGetStarted}
                className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 active:scale-95 text-white font-bold py-4 sm:py-5 px-6 sm:px-10 rounded-xl text-sm sm:text-lg shadow-2xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center touch-manipulation min-h-[56px]"
              >
                <span className="block sm:hidden text-center">{mobileCTAText}</span>
                <span className="hidden sm:block text-center">{ctaText}</span>
                <Zap className="w-4 h-4 sm:w-5 sm:h-5 ml-2 sm:ml-3 flex-shrink-0" />
              </button>
              <button
                onClick={onWatchDemo}
                className="border-2 border-white/40 hover:border-white/70 active:scale-95 text-white font-medium py-3 sm:py-4 px-6 sm:px-8 rounded-lg text-sm sm:text-base backdrop-blur-sm hover:backdrop-blur-md transition-all duration-200 touch-manipulation min-h-[48px] opacity-90"
              >
                Watch 2-Min Demo
              </button>
            </div>

            {/* Key Stat - Simplified for mobile */}
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-yellow-400 mb-2">10M+ Files Processed</div>
              <div className="text-sm sm:text-base text-blue-200">Photos & Videos • 99.2% accuracy • 30sec average processing time</div>
            </div>
          </div>

          {/* Right Column - Demo Video/Image - Hidden on mobile */}
          <div className="relative animate-in fade-in slide-in-from-right duration-1000 delay-300 hidden lg:block">
            {/* Video/Demo Container */}
            <div className="relative bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl">
              {/* Mock Interface Preview */}
              <div className="bg-white rounded-lg overflow-hidden shadow-xl">
                {/* Mock browser header */}
                <div className="bg-gray-100 px-4 py-3 flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  <div className="flex-1 text-center text-sm text-gray-600">
                    StockTagger.ai - AI Stock Photo Metadata Generator
                  </div>
                </div>
                
                {/* Mock interface content */}
                <div className="p-6">
                  <div className="space-y-4">
                    <div className="h-4 bg-blue-100 rounded animate-pulse"></div>
                    <div className="h-32 bg-gray-100 rounded flex items-center justify-center">
                      <div className="text-gray-400 text-sm">Upload Photos → AI Processing → SEO Metadata</div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-3 bg-green-100 rounded w-3/4 animate-pulse"></div>
                      <div className="h-3 bg-green-100 rounded w-1/2 animate-pulse"></div>
                      <div className="h-3 bg-green-100 rounded w-2/3 animate-pulse"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating success indicators */}
              <div className="absolute -top-2 sm:-top-4 -right-2 sm:-right-4 bg-green-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-bold animate-bounce flex items-center space-x-1">
                <Check className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden xs:inline">Processing Complete</span>
                <span className="xs:hidden">Done</span>
              </div>
              <div className="absolute -bottom-2 sm:-bottom-4 -left-2 sm:-left-4 bg-blue-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-bold flex items-center space-x-1">
                <Rocket className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden xs:inline">20x Faster</span>
                <span className="xs:hidden">Fast</span>
              </div>
            </div>

            {/* Background decorative elements */}
            <div className="absolute -top-8 -right-8 w-24 h-24 bg-yellow-400/20 rounded-full blur-xl"></div>
            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-purple-400/20 rounded-full blur-xl"></div>
          </div>
        </div>

        {/* Trust Indicators - Animated Carousel */}
        <div className="mt-16 pt-12 border-t border-white/20">
          <p className="text-center text-blue-200 mb-8">Trusted by photographers at:</p>

          {/* Carousel Container with Fade Edges */}
          <div
            className="relative overflow-hidden"
            style={{
              maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
              WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)'
            }}
          >
            <div className="flex gap-12 sm:gap-16 items-center animate-scroll-logos">
              {/* First Set */}
              <ShutterstockLogo className="h-6 sm:h-8 grayscale brightness-0 invert opacity-60 flex-shrink-0" />
              <GettyImagesLogo className="h-6 sm:h-8 grayscale brightness-0 invert opacity-60 flex-shrink-0" />
              <AdobeStockLogo className="h-6 sm:h-8 grayscale brightness-0 invert opacity-60 flex-shrink-0" />
              <UnsplashLogo className="h-6 sm:h-8 grayscale brightness-0 invert opacity-60 flex-shrink-0" />
              <IStockLogo className="h-6 sm:h-8 grayscale brightness-0 invert opacity-60 flex-shrink-0" />

              {/* Duplicate Set for Seamless Loop */}
              <ShutterstockLogo className="h-6 sm:h-8 grayscale brightness-0 invert opacity-60 flex-shrink-0" />
              <GettyImagesLogo className="h-6 sm:h-8 grayscale brightness-0 invert opacity-60 flex-shrink-0" />
              <AdobeStockLogo className="h-6 sm:h-8 grayscale brightness-0 invert opacity-60 flex-shrink-0" />
              <UnsplashLogo className="h-6 sm:h-8 grayscale brightness-0 invert opacity-60 flex-shrink-0" />
              <IStockLogo className="h-6 sm:h-8 grayscale brightness-0 invert opacity-60 flex-shrink-0" />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}